import { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;

  window.__turnstileLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return window.__turnstileLoading;
}

/** Cloudflare Turnstile widget (§10.5, decision resolved — see §12). */
export default function TurnstileWidget({ siteKey, onVerify, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onVerify,
        "expired-callback": onExpire,
        // Cloudflare's default widget is a fixed 300px wide. In the contact
        // and donation forms the column is 280px on a 360px screen, so the
        // widget pushed the whole page sideways by 20px. "flexible" makes
        // it fill its container instead.
        size: "flexible",
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [siteKey, onVerify, onExpire]);

  // data-hydration-async marks this for prerender.js to reset to empty
  // right before capturing the snapshot: the widget renders itself into
  // this container outside React's output, so the snapshot would otherwise
  // freeze whatever Turnstile had already drawn, while hydration compares
  // against the empty pre-effect render.
  // `size: "flexible"` above is honoured by newer Turnstile builds; where
  // it is not, the widget stays a fixed 300px and would overflow a 280px
  // form column. Letting the wrapper scroll keeps that contained instead
  // of pushing the page sideways — the same treatment wide tables get.
  return <div ref={containerRef} data-hydration-async="true" style={{ maxWidth: "100%", overflowX: "auto" }} />;
}
